import React, { useCallback, useContext, useEffect, useState } from "react";
import { fetchQuery, graphql } from "react-relay";
import { useHistory } from "react-router-dom";
import Select, {
	components,
	type DropdownIndicatorProps,
	type InputActionMeta,
	type MenuListProps,
	type MenuProps,
	type MultiValueGenericProps,
	type MultiValueRemoveProps,
	type OnChangeValue,
	type OptionProps,
	type Props,
	components as ReactSelectComponents,
	type SingleValueProps,
	type StylesConfig,
} from "react-select";
import AsyncSelect from "react-select/async";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faCaretDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import AppContext from "~/common/AppContext";
import { zindexDropdown } from "~/css/zindexes";
import { decodeGlobalId } from "~/lib/relayUtil";
import { nGetText, type NodesFromEdges } from "~/lib/utils";
import { environment } from "~/models";
import Avatar from "~/ui/legacy/Avatar";
import { ButtonLink } from "~/ui/legacyFellowIndex";

import type { AssigneeFilterInitialUsersQuery } from "~/__generated__/AssigneeFilterInitialUsersQuery.graphql";
import type { AssigneeFilterQuery, AssigneeFilterQuery$data } from "~/__generated__/AssigneeFilterQuery.graphql";

import styles from "~/common/css/AssigneeFilter.module.css";
import dropdownStyles from "~/common/css/Dropdown.module.css";

export type UserType = NodesFromEdges<AssigneeFilterQuery$data["selectUsers"]>;
export type TeamType = NodesFromEdges<AssigneeFilterQuery$data["teams"]>;
export type AssigneeType = {
	user?: UserType;
	team?: TeamType;
};

export const initialAssigneesQuery = graphql`
	query AssigneeFilterInitialUsersQuery($userIds: [ID], $teamIds: [ID]) {
		usersById(userIds: $userIds) {
			id
			email
			fullName
			image
			isGuest
		}

		teamsById(teamIds: $teamIds) {
			id
			title
		}
	}
`;

const assigneesQuery = graphql`
	query AssigneeFilterQuery($query: String) {
		selectUsers: users(first: 15, search: $query, isGuests: false) {
			edges {
				node {
					id
					email
					image
					fullName
					isGuest
				}
			}
		}

		teams(first: 10, query: $query) {
			edges {
				node {
					id
					title
				}
			}
		}
	}
`;

function DropdownIndicator(props: DropdownIndicatorProps<AssigneeType, boolean>) {
	return (
		<div
			{...props.innerProps}
			className={cx(dropdownStyles.caret, {
				[dropdownStyles.caretOpen]: props.selectProps.menuIsOpen,
				[dropdownStyles.disabled]: props.isDisabled,
			})}
		>
			<FontAwesomeIcon icon={faCaretDown} />
		</div>
	);
}

function UserRow(props: OptionProps<AssigneeType, boolean>) {
	const assignee = props.data as AssigneeType;

	if (assignee.user) {
		const user = assignee.user;

		return (
			<div className={styles.searchUserRow} {...props.innerProps} key={`user-${user.id}`}>
				<Avatar className="image" user={user} link={false} width="24px" />
				<span>{user.fullName}</span>
			</div>
		);
	}

	if (assignee.team) {
		const team = assignee.team;

		return (
			<div className={styles.searchUserRow} {...props.innerProps} key={`user-${team.id}`}>
				<Avatar
					className="image"
					team={team}
					link={false}
					width="24px"
					uiAvatarOptions={{ textForColor: team.title }}
				/>
				<span>{team.title}</span>
			</div>
		);
	}

	return null;
}

function MultiValueContainer(props: MultiValueGenericProps<AssigneeType>) {
	return <div className={styles.optionWrapper}>{props.children}</div>;
}

function SingleValue(props: SingleValueProps<AssigneeType>) {
	const assignee = props.data as AssigneeType;
	const RSSingleValue = components.SingleValue;

	if (assignee.user) {
		const user = assignee.user;

		return (
			<RSSingleValue {...props} className={styles.singleOptionLabel}>
				<Avatar className="image" user={user} link={false} width="24px" />
				<span>{user.fullName}</span>
			</RSSingleValue>
		);
	}

	if (assignee.team) {
		const team = assignee.team;

		return (
			<RSSingleValue {...props} className={styles.singleOptionLabel}>
				<Avatar
					className="image"
					team={team}
					link={false}
					width="24px"
					uiAvatarOptions={{ textForColor: team.title }}
				/>
				<span>{team.title}</span>
			</RSSingleValue>
		);
	}

	return null;
}

function MultiValueLabel(props: MultiValueGenericProps<AssigneeType>) {
	const assignee = props.data as AssigneeType;

	if (assignee.user) {
		const user = assignee.user;

		return (
			<div className={styles.optionLabel}>
				<Avatar className="image" user={user} link={false} width="24px" />
				<span>{user.fullName}</span>
			</div>
		);
	}

	if (assignee.team) {
		const team = assignee.team;

		return (
			<div className={styles.optionLabel}>
				<Avatar
					className="image"
					team={team}
					link={false}
					width="24px"
					uiAvatarOptions={{ textForColor: team.title }}
				/>
				<span>{team.title}</span>
			</div>
		);
	}

	return null;
}

function MultiValueCross(props: MultiValueRemoveProps<AssigneeType>) {
	if (props.selectProps.isDisabled) {
		return null;
	}

	return (
		<div {...props.innerProps} className={styles.optionCross}>
			<FontAwesomeIcon icon={faTimes} />
		</div>
	);
}

function CustomMenu(props: MenuProps<AssigneeType>) {
	return (
		<ReactSelectComponents.Menu {...props} className={styles.menu}>
			{props.children}
		</ReactSelectComponents.Menu>
	);
}

type CustomMenuListProps = {
	hideTeams?: boolean;
} & MenuListProps<AssigneeType>;

function CustomMenuList(props: CustomMenuListProps) {
	const { user } = useContext(AppContext);
	const hasAdminAccess = !!user?.hasAdminAccess;
	const history = useHistory();
	const itemsCount = props.getValue().length;

	const navigateToManageTeamsPage = useCallback(() => {
		history.push("/account/teams/");
	}, [history]);

	return (
		<ReactSelectComponents.MenuList {...props} className={styles.menuList}>
			<div className={cx({ [styles.assigneeMenu]: itemsCount > 0 })}>
				{props.children}

				{hasAdminAccess && !props.hideTeams ? (
					<div className={styles.footerSummary}>
						<div className={styles.createNewTeamButton}>
							<ButtonLink onClick={navigateToManageTeamsPage}>Manage teams</ButtonLink>
						</div>
					</div>
				) : (
					itemsCount > 0 && (
						<div className={styles.footerSummary}>
							<div className={styles.itemsCount}>{`${itemsCount} ${nGetText(itemsCount, "Item", "Items")}`}</div>
							<div className={styles.clearLink} onClick={props.clearValue}>
								Clear
							</div>
						</div>
					)
				)}
			</div>
		</ReactSelectComponents.MenuList>
	);
}

type AssigneeFilterProps = {
	initialAssigneeIds?: readonly string[] | null;
	usersToHide?: readonly string[] | null;
	values: AssigneeType[];
	setValues: (assignees: AssigneeType[]) => void;
	className?: string;
	placeholder?: string;
	isMulti?: boolean;
	hideTeams?: boolean;
} & Props<AssigneeType, boolean>;

export default function AssigneeFilter({
	initialAssigneeIds,
	usersToHide,
	values,
	setValues,
	className,
	menuPlacement,
	placeholder,
	isMulti = true,
	hideTeams,
	...rest
}: AssigneeFilterProps) {
	const [initialValuesLoading, setInitialValuesLoading] = useState(
		!!initialAssigneeIds && initialAssigneeIds.length > 0,
	);
	const [inputValue, setInputValue] = useState("");

	useEffect(() => {
		if (!!initialAssigneeIds && initialAssigneeIds.length > 0) {
			const userIds = initialAssigneeIds.filter(i => decodeGlobalId(i)[0] === "User");
			const teamIds = initialAssigneeIds.filter(i => decodeGlobalId(i)[0] === "Team");

			fetchQuery<AssigneeFilterInitialUsersQuery>(environment, initialAssigneesQuery, {
				userIds: userIds,
				teamIds: teamIds,
			})
				.toPromise()
				.then(response => {
					if (!response || !response.teamsById || !response.usersById) {
						return;
					}
					setValues(
						hideTeams
							? [...response.usersById.map(user => ({ user }))]
							: [...response.teamsById.map(team => ({ team })), ...response.usersById.map(user => ({ user }))],
					);
					setInitialValuesLoading(false);
				});
		}
	}, [hideTeams]); // eslint-disable-line react-hooks/exhaustive-deps

	const onInputChange = useCallback(
		(val: string, meta: InputActionMeta) => {
			if (meta.action === "input-change") {
				setInputValue(val);
			}
		},
		[setInputValue],
	);

	const onValueChange = useCallback(
		(val: OnChangeValue<AssigneeType, boolean>) => {
			if (val && "length" in val) {
				setValues((val || []) as AssigneeType[]);
			} else {
				setValues((val ? [val] : null) as AssigneeType[]);
			}
			setInputValue("");
		},
		[setValues, setInputValue],
	);

	const getOptionValue = useCallback((a: AssigneeType) => a.user?.id || a.team?.id || "", []);

	const loadOptions = useCallback(
		(query: string) => {
			return fetchQuery<AssigneeFilterQuery>(environment, assigneesQuery, { query })
				.toPromise()
				.then(response => {
					if (!response?.selectUsers?.edges || !response.teams?.edges) {
						return [];
					}
					if (hideTeams)
						return [
							...response.selectUsers.edges
								.map(edge => ({ user: edge.node }))
								.filter(({ user }) => !usersToHide || !usersToHide.includes(user.id)),
						];
					return [
						...response.teams.edges.map(edge => ({ team: edge.node })),
						...response.selectUsers.edges
							.map(edge => ({ user: edge.node }))
							.filter(({ user }) => !usersToHide || !usersToHide.includes(user.id)),
					];
				});
		},
		[hideTeams, usersToHide],
	);

	const customStyles: StylesConfig<AssigneeType, boolean> = {
		control: _base => ({
			backgroundColor: "initial",
			minHeight: "initial",
			display: "flex",
			height: "34.78px",
			border: "1px solid var(--inputBorderColor)",
			borderRadius: "6px",
			cursor: "pointer",
			"&:hover > div:last-child": {
				background: "var(--veryMutedGrey)",
				color: "initial",
				borderTopRightRadius: "4px",
				borderBottomRightRadius: "4px",
			},
			"&> div:last-child": {
				background: "initial",
				color: "initial",
				transform: "rotate(0)",
			},
		}),
		valueContainer: () => ({
			flex: "1",
			height: "32.78px",
			padding: "0 4px",
			display: isMulti ? "flex" : "grid",
			alignItems: "center",
			overflow: "hidden",
			cursor: "pointer",
		}),
		menu: base => ({
			...base,
			background: "var(--secondLayerColor)",
			maxHeight: "250px",
			borderRadius: "4px 4px 0 0",
		}),
		menuList: base => ({
			...base,
			maxHeight: "250px",
		}),
		dropdownIndicator: base => ({
			...base,
			cursor: "pointer",
		}),
		singleValue: base => ({
			...base,
			color: "var(--primaryTextColor)",
		}),
		placeholder: base => ({
			...base,
			position: "absolute",
		}),
		menuPortal: base => ({
			...base,
			zIndex: zindexDropdown,
		}),
	};

	if (initialValuesLoading) {
		return <Select isDisabled className={cx(styles.filter, className)} placeholder="Loading..." />;
	}

	return (
		<AsyncSelect<AssigneeType, boolean>
			{...rest}
			defaultOptions
			blurInputOnSelect={!isMulti}
			isMulti={isMulti}
			isClearable={false}
			value={values}
			onChange={onValueChange}
			inputValue={inputValue}
			onInputChange={onInputChange}
			components={{
				IndicatorSeparator: null,
				Option: UserRow,
				MultiValueContainer: MultiValueContainer,
				MultiValueLabel: MultiValueLabel,
				MultiValueRemove: MultiValueCross,
				SingleValue,
				Menu: CustomMenu,
				MenuList: (props: CustomMenuListProps) => <CustomMenuList {...props} hideTeams={hideTeams} />,
				DropdownIndicator,
			}}
			placeholder={placeholder || "All teams & users"}
			className={cx(styles.filter, className)}
			classNamePrefix="assignee-selector"
			styles={customStyles}
			loadOptions={loadOptions}
			getOptionValue={getOptionValue}
			menuPlacement={menuPlacement || "bottom"}
		/>
	);
}
